import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { getSupabase, resetSupabaseAuth, SUPABASE_URL } from '../supabase.js';
import { AUTH_STORAGE_KEY, getPersistedAccountId, isCurrentPersistedSession, readOfflineSession } from './sessionStorage.js';
import { resolveActiveTenantId } from './effectiveTenant.js';
import {
  getActiveSharedTabletOperator,
  subscribeActiveSharedTabletOperator,
} from './activeOperatorSession.js';
import type { ActiveSharedTabletOperator } from './operatorCredentialStore.js';

export type NiveoRole = 'consulente' | 'ristoratore' | 'osa' | 'staff';

// T-266: motivo per cui l'account è bloccato, dal claim JWT `account_block_reason`
// (hook 20260616120000). 'suspended' → account sospeso, 'trial_expired' → trial
// scaduto, null → operativo.
export type AccountBlockReason = 'suspended' | 'trial_expired' | null;

export interface UserProfile {
  id: string;
  email: string | null;
  full_name: string | null;
  role: NiveoRole | null;
  // Ruolo globale legacy: solo navigazione/control-plane. Le autorizzazioni
  // del tenant usano sempre `role` (claim app_role della membership attiva).
  global_role: NiveoRole | null;
  tenants: string[];
  active_tenant_id: string | null;
  active_business_id: string | null;
  // Platform owner unico (T-007): claim JWT `is_platform_owner` popolato dalla
  // migration 20260519220000. Serve a sbloccare la UI per l'owner senza dover
  // controllare l'email del profilo nei componenti.
  is_platform_owner: boolean;
  // Trial 10 giorni (T-019): claim JWT popolati dalla migration 20260520120000.
  // `trial_ends_at` è ISO timestamp del momento di scadenza (o null se il
  // tenant non è più in trial / non ha scadenza). `trial_active` è la
  // valutazione server-side a freddo del momento di emissione del token:
  // false → hard-block in AuthGate. Sessioni emesse prima del backfill non
  // hanno questi claim: trattali come `trial_active: true` per non bloccare.
  trial_ends_at: string | null;
  trial_active: boolean;
  // T-266: motivo di blocco preciso dal JWT hook. 'suspended' → SuspendedPage,
  // 'trial_expired' → TrialExpiredPage, null → operativo. Token emessi prima di
  // T-266 non hanno il claim → null; AuthGate usa `trial_active` come fallback
  // (un blocco senza motivo viene trattato come trial scaduto) per non sbloccare.
  account_block_reason: AccountBlockReason;
  // T-282: claim `preposto` (utente ESTERNO scoped a UNA destinazione di
  // collettiva). Presente SOLO per i preposti; null per tutti gli altri. Se
  // valorizzato, AuthGate mostra la shell minimale "ordine preposto" e NON la
  // app HACCP (il preposto ha `tenants: []` → fail-closed lato server).
  preposto: { tenant_id: string; destination_id: string } | null;
}

export function effectiveProfileForOperator(
  profile: UserProfile | null,
  operator: ActiveSharedTabletOperator | null,
): UserProfile | null {
  if (!profile || !operator) return profile;
  return {
    ...profile,
    full_name: operator.fullName,
    role: operator.appRole,
  };
}

export function missingActiveBusinessTenantId(
  profile: UserProfile | null,
): string | null {
  const tenantId = profile?.active_tenant_id ?? null;
  return tenantId && profile && profile.active_business_id == null && profile.tenants.includes(tenantId)
    ? tenantId
    : null;
}

export interface BootstrapInput {
  fullName: string;
  role: NiveoRole;
  tenantName: string;
  tenantPiva?: string;
  businessName: string;
}

export interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<{ alreadyRegistered: boolean }>;
  signOut: () => Promise<void>;
  bootstrapTenant: (input: BootstrapInput) => Promise<{ tenant_id: string }>;
  refreshProfile: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  switchBusiness: (businessId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface DecodedClaims {
  sub?: string;
  email?: string;
  role?: string;
  app_role?: string;
  global_role?: string;
  tenant_id?: string | null;
  tenants?: string[];
  active_business_id?: string | null;
  is_platform_owner?: boolean;
  trial_ends_at?: string | null;
  trial_active?: boolean;
  account_block_reason?: string | null;
  preposto?: { tenant_id?: string; destination_id?: string } | null;
  user_metadata?: Record<string, unknown>;
}

function decodeAccessToken(token: string | null | undefined): DecodedClaims | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const decoded = atob(padded);
    return JSON.parse(decoded) as DecodedClaims;
  } catch {
    return null;
  }
}

function isNiveoRole(x: unknown): x is NiveoRole {
  return x === 'consulente' || x === 'ristoratore' || x === 'osa' || x === 'staff';
}

function normalizeBlockReason(x: unknown): AccountBlockReason {
  return x === 'suspended' || x === 'trial_expired' ? x : null;
}

function profileFromSession(session: Session | null): UserProfile | null {
  if (!session?.user) return null;
  const claims = decodeAccessToken(session.access_token) ?? {};
  const userMeta = (session.user.user_metadata ?? {}) as Record<string, unknown>;
  const tenants = Array.isArray(claims.tenants) ? claims.tenants.filter((t): t is string => typeof t === 'string') : [];
  const appRole = claims.app_role ?? claims.role;
  const role = isNiveoRole(appRole) ? appRole : null;
  const globalRole = isNiveoRole(claims.global_role) ? claims.global_role : role;
  const activeTenantId = resolveActiveTenantId(claims as Record<string, unknown>, tenants);
  const activeBusinessId =
    typeof claims.active_business_id === 'string' ? claims.active_business_id : null;
  const fullName = typeof userMeta.full_name === 'string' ? userMeta.full_name : null;
  // Il claim è popolato dalla migration 20260519220000 (T-007). Sessioni
  // emesse prima del backfill non hanno il claim → false. Serve re-login.
  const isPlatformOwner = claims.is_platform_owner === true;
  // Trial 10 giorni (T-019): claim popolati dalla migration 20260520120000.
  // Sessioni più vecchie senza il claim → consideriamo trial_active=true per
  // non bloccare erroneamente; al primo refresh del token i claim arrivano.
  const trialEndsAt = typeof claims.trial_ends_at === 'string' ? claims.trial_ends_at : null;
  const trialActive = claims.trial_active === undefined ? true : claims.trial_active === true;
  const accountBlockReason = normalizeBlockReason(claims.account_block_reason);
  const preposto =
    claims.preposto &&
    typeof claims.preposto.tenant_id === 'string' &&
    typeof claims.preposto.destination_id === 'string'
      ? { tenant_id: claims.preposto.tenant_id, destination_id: claims.preposto.destination_id }
      : null;
  return {
    id: session.user.id,
    email: session.user.email ?? null,
    full_name: fullName,
    role,
    global_role: globalRole,
    tenants,
    active_tenant_id: activeTenantId,
    active_business_id: activeBusinessId,
    is_platform_owner: isPlatformOwner,
    trial_ends_at: trialEndsAt,
    trial_active: trialActive,
    account_block_reason: accountBlockReason,
    preposto,
  };
}

const PROFILE_CACHE_KEY = 'niveo.profile_cache_v1';

function loadCachedProfile(): UserProfile | null {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<UserProfile>;
    if (!parsed || typeof parsed !== 'object' || !parsed.id) return null;
    // Backfill per cache emessa prima di T-011: `is_platform_owner` potrebbe
    // mancare → default `false`. Per T-019: cache senza `trial_active` →
    // default `true` (non bloccare per cache obsoleta; il refresh del JWT
    // sostituirà i valori).
    return {
      ...parsed,
      global_role: isNiveoRole(parsed.global_role) ? parsed.global_role : (isNiveoRole(parsed.role) ? parsed.role : null),
      is_platform_owner: parsed.is_platform_owner === true,
      trial_ends_at: typeof parsed.trial_ends_at === 'string' ? parsed.trial_ends_at : null,
      trial_active: parsed.trial_active === undefined ? true : parsed.trial_active === true,
      account_block_reason: normalizeBlockReason(parsed.account_block_reason),
      preposto: parsed.preposto ?? null,
    } as UserProfile;
  } catch {
    return null;
  }
}

function saveCachedProfile(p: UserProfile | null): void {
  try {
    if (p) localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(p));
    else localStorage.removeItem(PROFILE_CACHE_KEY);
  } catch { /* noop */ }
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [supabase, setSupabase] = useState(getSupabase);
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(() => readOfflineSession(SUPABASE_URL));
  // Offline identity comes only from the actual persisted SDK session. The
  // online profile cache remains behind the loading/auth gate until refreshed.
  const [profile, setProfile] = useState<UserProfile | null>(() => navigator.onLine
    ? loadCachedProfile() : profileFromSession(session));
  const [loading, setLoading] = useState(navigator.onLine);
  const mounted = useRef(true);
  const authTransition = useRef(false);
  const accountId = useRef(getPersistedAccountId(SUPABASE_URL));
  const mediaPurge = useRef<Promise<void> | null>(null);
  const purgeAccountMedia = useCallback((): Promise<void> => {
    if (mediaPurge.current) return mediaPurge.current;
    const pending = (async () => {
      const { purgeProductionLotPhotoOutbox } = await import('../labels/productionLotPhotoOutbox.js');
      await purgeProductionLotPhotoOutbox();
      const { purgeOperationalEvidenceOutsideTenants } = await import('../haccp/operationalEvidenceOutbox.js');
      await purgeOperationalEvidenceOutsideTenants([]);
    })();
    mediaPurge.current = pending;
    void pending.finally(() => {
      if (mediaPurge.current === pending) mediaPurge.current = null;
    }).catch(() => undefined);
    return pending;
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const applySession = useCallback((nextSession: Session | null, source: typeof supabase): boolean => {
    if (!mounted.current || authTransition.current || source !== getSupabase()
      || !isCurrentPersistedSession(nextSession, SUPABASE_URL)) return false;
    accountId.current = nextSession?.user.id ?? null;
    setSession(nextSession);
    const nextProfile = profileFromSession(nextSession);
    setProfile(nextProfile);
    saveCachedProfile(nextProfile);
    return true;
  }, []);

  useEffect(() => {
    let active = true;
    let request = 0;
    const isCurrent = () => active && !authTransition.current && supabase === getSupabase();
    const transitionAccount = (preserveSession: boolean): void => {
      // Retire before awaiting the privacy boundary. Neither an old refresh nor
      // a new external login may enable the app while the purge is unfinished.
      authTransition.current = true;
      resetSupabaseAuth(!preserveSession);
      const nextClient = getSupabase();
      setSupabase(nextClient);
      accountId.current = null;
      setSession(null);
      setProfile(null);
      saveCachedProfile(null);
      setLoading(true);
      queryClient.clear();
      void purgeAccountMedia().then(async () => {
        if (nextClient !== getSupabase() || !mounted.current) return;
        authTransition.current = false;
        const next = navigator.onLine
          ? (await nextClient.auth.getSession()).data.session : readOfflineSession(SUPABASE_URL);
        if (nextClient !== getSupabase() || !mounted.current) return;
        applySession(next, nextClient);
        setLoading(false);
      }).catch(() => {
        // If the local purge failed, keep the auth barrier closed. An explicit
        // new login repeats the purge; an external auth event cannot skip it.
        if (nextClient === getSupabase() && mounted.current) setLoading(false);
      });
    };
    const acceptSession = (nextSession: Session | null): boolean => {
      if (!isCurrent() || !isCurrentPersistedSession(nextSession, SUPABASE_URL)) return false;
      if (nextSession && accountId.current && nextSession.user.id !== accountId.current) {
        transitionAccount(true);
        return false;
      }
      return applySession(nextSession, supabase);
    };
    const readFreshSession = async (): Promise<void> => {
      if (!isCurrent()) return;
      const ownRequest = ++request;
      try {
        if (!navigator.onLine) {
          if (isCurrent()) acceptSession(readOfflineSession(SUPABASE_URL));
          return;
        }
        const current = await supabase.auth.getSession();
        if (!isCurrent() || ownRequest !== request || current.error) return;
        const refreshed = current.data.session
          ? await supabase.auth.refreshSession().catch(() => null) : null;
        if (isCurrent() && ownRequest === request) {
          acceptSession(refreshed?.data.session ?? current.data.session);
        }
      } catch {
        // Transport failure is not logout. Definitive SDK rejection removes
        // its session and emits SIGNED_OUT, which is handled below.
      } finally {
        if (isCurrent() && ownRequest === request) setLoading(false);
      }
    };
    void readFreshSession();
    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!acceptSession(newSession)) return;
      setLoading(false);
      if (event === 'SIGNED_OUT') transitionAccount(false);
    });

    // Capacitor / WebView Android: i timer JS vengono sospesi quando l'app va
    // in background, quindi `autoRefreshToken: true` smette di rinnovare il
    // JWT alla scadenza (default 1h). Al ritorno in foreground il token è
    // scaduto → la prima sync-pull fallisce con 401 ("errore sync"). Workaround
    // documentato da Supabase: stop/startAutoRefresh sull'evento appStateChange,
    // più un refresh forzato al rientro per non aspettare il prossimo tick.
    let removeAppListener: (() => void) | null = null;
    const onResume = (): void => {
      if (!isCurrent()) return;
      if (navigator.onLine) void supabase.auth.startAutoRefresh().catch(() => undefined);
      else void supabase.auth.stopAutoRefresh().catch(() => undefined);
      void readFreshSession();
    };
    const onPause = (): void => {
      void supabase.auth.stopAutoRefresh().catch(() => undefined);
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') onResume();
      else onPause();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onResume);
    window.addEventListener('offline', onResume);
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== AUTH_STORAGE_KEY && event.key !== null) return;
      if (isCurrent() && isCurrentPersistedSession(null, SUPABASE_URL)) transitionAccount(false);
      else onResume();
    };
    window.addEventListener('storage', onStorage);
    // Capacitor App listener (Android/iOS native lifecycle). Importato dinamicamente
    // perché su web/PWA il modulo nativo non è disponibile.
    void (async (): Promise<void> => {
      try {
        const mod = await import('@capacitor/app');
        const handle = await mod.App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) onResume(); else onPause();
        });
        if (!active) void handle.remove();
        else removeAppListener = (): void => { void handle.remove(); };
      } catch { /* PWA o modulo assente: usiamo solo visibilitychange */ }
    })();

    return () => {
      active = false;
      request += 1;
      sub.subscription.unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onResume);
      window.removeEventListener('offline', onResume);
      window.removeEventListener('storage', onStorage);
      if (removeAppListener) removeAppListener();
    };
  }, [applySession, purgeAccountMedia, queryClient, supabase]);

  const beginAccountChange = useCallback(() => {
    resetSupabaseAuth();
    authTransition.current = true;
    accountId.current = null;
    setSession(null);
    setProfile(null);
    saveCachedProfile(null);
    queryClient.clear();
    const nextClient = getSupabase();
    setSupabase(nextClient);
    return nextClient;
  }, [queryClient]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const nextClient = beginAccountChange();
      await purgeAccountMedia();
      if (nextClient !== getSupabase()) throw new Error('auth_session_changed');
      authTransition.current = false;
      const { data, error } = await nextClient.auth.signInWithPassword({ email, password });
      if (nextClient !== getSupabase()) throw new Error('auth_session_changed');
      if (error) throw error;
      applySession(data.session, nextClient);
    },
    [applySession, beginAccountChange, purgeAccountMedia],
  );

  // Login/registrazione con Google. Su nativo (Capacitor) NON si può fare
  // OAuth dentro la WebView (Google lo blocca: disallowed_useragent), quindi
  // apriamo una Custom Tab di sistema e il ritorno avviene via deep link
  // `trackeat://auth-callback?code=…` gestito dal DeepLinkHandler (PKCE).
  // Su web/PWA usiamo il redirect classico same-origin.
  const signInWithGoogle = useCallback(async () => {
    const nextClient = beginAccountChange();
    await purgeAccountMedia();
    const { Capacitor } = await import('@capacitor/core');
    if (nextClient !== getSupabase()) throw new Error('auth_session_changed');
    authTransition.current = false;
    const isNative = Capacitor.isNativePlatform();
    if (isNative) {
      const { data, error } = await nextClient.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: 'trackeat://auth-callback',
          skipBrowserRedirect: true,
        },
      });
      if (nextClient !== getSupabase()) throw new Error('auth_session_changed');
      if (error) throw error;
      if (!data?.url) throw new Error('OAuth URL mancante');
      const { Browser } = await import('@capacitor/browser');
      if (nextClient !== getSupabase()) throw new Error('auth_session_changed');
      await Browser.open({ url: data.url, presentationStyle: 'popover' });
      return;
    }
    const { error } = await nextClient.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/today` },
    });
    if (error) throw error;
  }, [beginAccountChange, purgeAccountMedia]);

  const signUp = useCallback(
    async (email: string, password: string) => {
      const nextClient = beginAccountChange();
      await purgeAccountMedia();
      if (nextClient !== getSupabase()) throw new Error('auth_session_changed');
      authTransition.current = false;
      const { data, error } = await nextClient.auth.signUp({ email, password });
      if (nextClient !== getSupabase()) throw new Error('auth_session_changed');
      if (error) throw error;
      // Supabase, per anti-user-enumeration, NON ritorna errore se l'email è
      // già registrata: ritorna invece un User con `identities: []` (array vuoto).
      // Documentato: https://supabase.com/docs/reference/javascript/auth-signup
      const identities = data.user?.identities ?? [];
      const alreadyRegistered = Boolean(data.user) && identities.length === 0;
      return { alreadyRegistered };
    },
    [beginAccountChange, purgeAccountMedia],
  );

  const signOut = useCallback(async () => {
    // Revoke only the original, still-current session. The public admin call
    // does not remove storage, so a later login in another tab is preserved.
    const logoutToken = session && isCurrentPersistedSession(session, SUPABASE_URL)
      ? session.access_token : null;
    authTransition.current = true;
    const online = navigator.onLine;
    resetSupabaseAuth();
    const logoutClient = getSupabase();
    setSupabase(logoutClient);
    setLoading(true);
    accountId.current = null;
    setSession(null);
    setProfile(null);
    saveCachedProfile(null);
    queryClient.clear();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let finishRemote = (): void => undefined;
    const remoteDone = new Promise<void>(resolve => { finishRemote = resolve; });
    window.addEventListener('offline', finishRemote, { once: true });
    if (online && logoutToken) {
      timer = setTimeout(finishRemote, 5_000);
      void Promise.allSettled([
        logoutClient.auth.admin.signOut(logoutToken, 'global'),
        import('../notifications/pushRegistration.js').then(({ unregisterPushToken }) => {
          if (logoutClient === getSupabase()) return unregisterPushToken();
        }),
      ]).then(finishRemote);
    } else finishRemote();
    let purged = false;
    try {
      // Pending unencrypted images must be purged before a later login. A
      // concurrent login joins this same purge before starting its auth call.
      await Promise.all([purgeAccountMedia().then(() => { purged = true; }), remoteDone]);
    } finally {
      if (timer) clearTimeout(timer);
      window.removeEventListener('offline', finishRemote);
      if (logoutClient === getSupabase()) {
        resetSupabaseAuth(false);
        setSupabase(getSupabase());
        authTransition.current = !purged;
        if (mounted.current) setLoading(false);
      }
    }
  }, [purgeAccountMedia, queryClient, session]);

  const bootstrapTenant = useCallback(
    async (input: BootstrapInput) => {
      const { data, error } = await supabase.rpc('bootstrap_new_user', {
        p_full_name: input.fullName,
        p_role: input.role,
        p_tenant_name: input.tenantName,
        p_tenant_piva: input.tenantPiva ?? null,
        p_business_name: input.businessName,
      });
      if (supabase !== getSupabase()) throw new Error('auth_session_changed');
      if (error) throw error;
      await supabase.auth.refreshSession();
      if (supabase !== getSupabase()) throw new Error('auth_session_changed');
      return { tenant_id: (data as string | null) ?? '' };
    },
    [supabase],
  );

  const refreshProfile = useCallback(async () => {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) throw error;
    if (!applySession(data.session, supabase)) throw new Error('auth_session_changed');
  }, [applySession, supabase]);

  const switchTenant = useCallback(
    async (tenantId: string) => {
      const { error } = await supabase.rpc('switch_active_tenant', { p_tenant_id: tenantId });
      if (supabase !== getSupabase()) throw new Error('auth_session_changed');
      if (error) throw error;
      const { data, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) throw refreshError;
      // Non attendere soltanto l'evento auth asincrono: durante lo switch la UI
      // deve costruire subito le query con tenant/JWT nuovi, non con il contesto
      // del ristorante appena lasciato.
      if (!applySession(data.session, supabase)) throw new Error('auth_session_changed');
      // Hard cache flush: cambiare tenant = "loggarsi come quel cliente".
      // Nessun dato del tenant precedente deve sopravvivere (anche se una
      // query non ha tenantId in queryKey, ora vede cache vuota e refetcha).
      queryClient.clear();
      // La pull usa il JWT appena aggiornato e ricostruisce il read model del
      // nuovo tenant senza richiedere un reload della PWA.
      void import('../sync/engine.js')
        .then(({ getSyncEngine }) => {
          if (supabase === getSupabase()) return getSyncEngine().tick();
        })
        .catch(() => undefined);
    },
    [applySession, supabase, queryClient],
  );

  const switchBusiness = useCallback(
    async (businessId: string) => {
      const { error } = await supabase.rpc('switch_active_business', { p_business_id: businessId });
      if (supabase !== getSupabase()) throw new Error('auth_session_changed');
      if (error) throw error;
      const { data, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) throw refreshError;
      if (!applySession(data.session, supabase)) throw new Error('auth_session_changed');
      queryClient.clear();
      void import('../sync/engine.js')
        .then(({ getSyncEngine }) => {
          if (supabase === getSupabase()) return getSyncEngine().tick();
        })
        .catch(() => undefined);
    },
    [applySession, supabase, queryClient],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      loading,
      signIn,
      signInWithGoogle,
      signUp,
      signOut,
      bootstrapTenant,
      refreshProfile,
      switchTenant,
      switchBusiness,
    }),
    [
      session,
      profile,
      loading,
      signIn,
      signInWithGoogle,
      signUp,
      signOut,
      bootstrapTenant,
      refreshProfile,
      switchTenant,
      switchBusiness,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  const operator = useSyncExternalStore(
    subscribeActiveSharedTabletOperator,
    getActiveSharedTabletOperator,
    () => null,
  );
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return useMemo(() => ({
    ...ctx,
    profile: effectiveProfileForOperator(ctx.profile, operator),
  }), [ctx, operator]);
}
